import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

import {header2Search} from '@amblique/king-living/app/theme/foundations/header-2-search';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'layoutRow',
  'mainColumn',
  'spotlightColumn',
  'spotlightList',
  'spotlightFirstContainer',
  'spotlightHeadingBlock',
  'spotlightHeading',
  'spotlightItem',
  'spotlightCard',
  'spotlightImage',
  'spotlightName',
  'popularSection',
  'popularHeading',
  'popularTags',
  'popularTag',
  'popularLink',
  'trendingSection',
  'trendingHeading',
  'trendingList',
  'trendingListMobile',
  'trendingListGrid',
  'trendingItem',
  'trendingItemInner',
  'trendingImage',
  'trendingInfo',
  'trendingTitle',
  'trendingCategory',
  'trendingGridItem',
  'trendingGridItemInner',
  'trendingGridImage',
  'trendingGridInfo',
  'trendingGridTitle',
  'trendingGridCategory',
]);

/** Header modal section labels — 10px mobile, 12px md+ */
const sectionLabel = {
  color: 'rgba(56, 56, 56, 0.6)',
  fontFamily: 'body',
  fontSize: {base: '10px', md: '12px'},
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: {base: '2px', md: '2.4px'},
  lineHeight: {base: 'normal', md: '120%'},
  textTransform: 'uppercase',
};

/** Search results eyebrows — Popular / Trending / Spotlight (xl = desktop) */
const searchSectionEyebrowText = {
  color: 'rgba(56, 56, 56, 0.6)',
  fontFamily: 'body',
  fontSize: {base: '10px', xl: '12px'},
  fontStyle: 'normal',
  fontWeight: 600,
  letterSpacing: {base: '2px', xl: '2.4px'},
  lineHeight: {base: 'normal', xl: '120%'},
  textTransform: 'uppercase',
};

/** Trending product tile — title + category (mobile list + desktop grid) */
const trendingProductTitleText = {
  color: 'brandColors.darkCharcoal',
  fontFamily: 'body',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '120%',
};

const trendingProductCategoryText = {
  color: 'rgba(0, 0, 0, 0.4)',
  fontFamily: 'body',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '120%',
};

/** Spotlight article title link */
const spotlightArticleTitleText = {
  color: 'brandColors.darkCharcoal',
  fontFamily: 'body',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 500,
};

const popularTagBase = {
  alignItems: 'center',
  bg: 'transparent',
  border: '1px solid',
  borderColor: 'background.linen03',
  borderRadius: '100px',
  color: 'greyscale.100',
  display: 'inline-flex',
  flexShrink: 0,
  fontFamily: 'body',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 600,
  height: '38px',
  justifyContent: 'center',
  lineHeight: '140%',
  minH: '38px',
  minW: 'auto',
  px: 4,
  py: 0,
  whiteSpace: 'nowrap',
  _hover: {
    bg: 'transparent',
    opacity: 0.85,
  },
  _active: {
    bg: 'transparent',
  },
  _focus: {
    boxShadow: 'none',
  },
  _focusVisible: {
    boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
  },
};

/** Reset Chakra `Button` md padding (px:6 → 24px) — apply on the button itself, not `& button` */
const trendingButtonResetSx = {
  border: 'none',
  boxShadow: 'none',
  height: 'auto',
  lineHeight: 'inherit',
  margin: '0',
  maxW: '100%',
  minH: 'unset',
  minW: '0',
  padding: '0',
  width: '100%',
};

const trendingItemBase = {
  alignItems: 'center',
  bg: 'background.secondary',
  display: 'flex',
  height: 'auto',
  justifyContent: 'stretch',
  minH: '75px',
  rounded: 'none',
  width: 'full',
  sx: {
    ...trendingButtonResetSx,
    minHeight: {base: '75px', md: '75px'},
    padding: {
      base: '16px',
      md: '14px 30px 13px 20px',
    },
  },
  _active: {
    bg: 'background.secondary',
  },
  _focus: {
    boxShadow: 'none',
  },
  _focusVisible: {
    boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
  },
  _hover: {
    bg: 'background.secondary',
    opacity: 0.92,
  },
};

/**
 * Search idle — header modal (default) + search results empty state (`searchResults` variant).
 */
export default helpers.defineMultiStyleConfig({
  baseStyle: {
    root: {
      maxW: '100%',
      mt: {base: '32px', md: 6},
      width: '100%',
    },
    layoutRow: {
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: {base: 'column', md: 'row'},
      gap: {base: 12, md: 10},
      justifyContent: 'space-between',
      width: 'full',
    },
    mainColumn: {
      flex: {base: 'unset', md: 1},
      maxW: {base: 'full', md: '619px'},
      minW: 0,
      width: {base: 'full', md: 'auto'},
    },
    spotlightColumn: {
      flexShrink: 0,
      maxW: {base: 'full', md: '291px'},
      minW: 0,
      width: {base: 'full', md: '291px'},
    },
    spotlightList: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
      width: 'full',
    },
    spotlightFirstContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: 'full',
    },
    spotlightHeadingBlock: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: 'full',
    },
    spotlightHeading: sectionLabel,
    spotlightItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      width: 'full',
    },
    spotlightCard: {
      bg: 'transparent',
      border: 'none',
      cursor: 'pointer',
      display: 'block',
      h: 'auto',
      m: 0,
      minH: 'auto',
      p: 0,
      rounded: 'none',
      textAlign: 'left',
      textDecoration: 'none',
      width: 'full',
      _active: {
        bg: 'transparent',
      },
      _focus: {
        boxShadow: 'none',
      },
      _focusVisible: {
        boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
      },
      _hover: {
        bg: 'transparent',
        opacity: 0.92,
      },
    },
    spotlightImage: {
      aspectRatio: {base: '16 / 9', md: 'unset'},
      backgroundColor: 'greyscale90Alpha.10',
      flexShrink: 0,
      minH: {base: 'unset', md: '176px'},
      overflow: 'hidden',
      position: 'relative',
      w: 'full',
      sx: {
        img: {
          height: '100%',
          minHeight: {base: 'unset', md: '176px'},
          objectFit: 'cover',
          width: '100%',
        },
      },
    },
    spotlightName: {
      color: 'brandColors.darkCharcoal',
      fontFamily: 'body',
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '140%',
      width: 'full',
    },
    popularSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: 'full',
    },
    popularHeading: sectionLabel,
    popularTags: {
      spacing: 2,
      width: 'full',
    },
    popularTag: popularTagBase,
    popularLink: {
      color: 'brandColors.primaryText90',
      display: 'block',
      fontFamily: 'body',
      fontSize: {base: '13px', md: '14px'},
      fontWeight: 400,
      height: 'auto',
      lineHeight: {base: 1.2, md: 1.4},
      minH: 'auto',
      px: 0,
      py: 0,
      textAlign: 'left',
      width: 'full',
      _hover: {
        bg: 'transparent',
        textDecoration: 'underline',
      },
    },
    trendingSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      width: 'full',
    },
    trendingHeading: sectionLabel,
    trendingList: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      width: 'full',
    },
    trendingItem: trendingItemBase,
    trendingItemInner: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: {base: 2, md: 2.5},
      minW: 0,
      pl: 0,
      pr: 0,
      width: 'full',
    },
    trendingImage: {
      backgroundColor: 'greyscale90Alpha.10',
      flexShrink: 0,
      h: '48px',
      overflow: 'hidden',
      position: 'relative',
      w: '108px',
    },
    trendingInfo: {
      alignItems: {base: 'flex-start', md: 'center'},
      display: 'flex',
      flex: 1,
      flexDirection: {base: 'column', md: 'row'},
      gap: {base: 2, md: 4},
      justifyContent: 'flex-start',
      minW: 0,
      textAlign: 'left',
    },
    trendingTitle: {
      ...trendingProductTitleText,
      flex: {base: 'unset', md: 1},
      minW: 0,
      textAlign: 'left',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
    trendingCategory: {
      ...trendingProductCategoryText,
      flexShrink: 0,
      maxW: {base: 'full', md: '170px'},
      textAlign: 'left',
      whiteSpace: 'nowrap',
    },
  },
  variants: {
    /** `/search` no-results idle panel — header search modal keeps default styles */
    searchResults: {
      root: {
        mt: 0,
      },
      layoutRow: {
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: {base: 'column', xl: 'row'},
        gap: {base: '48px', xl: '40px'},
        justifyContent: 'space-between',
        width: 'full',
      },
      mainColumn: {
        flex: {base: 'unset', xl: 1},
        maxW: '740px',
        minW: 0,
        width: '100%',
      },
      popularSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: 'full',
      },
      popularHeading: {
        ...searchSectionEyebrowText,
        mb: 0,
      },
      popularTags: {
        gap: '8px',
        spacing: '8px',
        width: 'full',
      },
      popularTag: {
        ...popularTagBase,
        fontWeight: 500,
      },
      trendingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: 'full',
      },
      trendingHeading: {
        ...searchSectionEyebrowText,
        mb: 0,
      },
      trendingListMobile: {
        alignItems: 'stretch',
        display: {base: 'flex', xl: 'none'},
        flexDirection: 'column',
        gap: 1,
        width: 'full',
        sx: {
          '& button': {
            ...trendingButtonResetSx,
            minHeight: '80px',
            padding: '16px',
          },
        },
      },
      trendingListGrid: {
        alignItems: 'stretch',
        columnGap: '16px',
        display: {base: 'none', xl: 'grid'},
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        rowGap: '24px',
        width: 'full',
        sx: {
          '& button': trendingButtonResetSx,
        },
      },
      trendingItem: {
        alignItems: 'center',
        bg: 'background.secondary',
        display: 'flex',
        flexDirection: 'row',
        h: 'auto',
        justifyContent: 'stretch',
        minH: '80px',
        rounded: 'none',
        width: 'full',
        sx: {
          ...trendingButtonResetSx,
          minHeight: '80px',
          padding: '16px',
        },
        _active: {
          bg: 'background.secondary',
        },
        _focus: {
          boxShadow: 'none',
        },
        _focusVisible: {
          boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
        },
        _hover: {
          bg: 'background.secondary',
          opacity: 0.92,
        },
      },
      trendingItemInner: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        gap: '8px',
        minW: 0,
        pl: 0,
        pr: 0,
        width: 'full',
      },
      trendingImage: {
        backgroundColor: 'greyscale90Alpha.10',
        flexShrink: 0,
        h: '48px',
        overflow: 'hidden',
        position: 'relative',
        w: '108px',
      },
      trendingTitle: {
        ...trendingProductTitleText,
        minW: 0,
        textAlign: 'left',
        whiteSpace: 'normal',
        wordBreak: 'break-word',
      },
      trendingCategory: {
        ...trendingProductCategoryText,
        flexShrink: 0,
        maxW: 'full',
        minW: 0,
        overflow: 'hidden',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      trendingGridItem: {
        alignItems: 'stretch',
        bg: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        h: 'auto',
        justifyContent: 'flex-start',
        minH: 'unset',
        rounded: 'none',
        width: 'full',
        sx: {
          ...trendingButtonResetSx,
          padding: '0',
        },
        _active: {
          bg: 'transparent',
        },
        _focus: {
          boxShadow: 'none',
        },
        _focusVisible: {
          boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
        },
        _hover: {
          bg: 'transparent',
          opacity: 0.92,
        },
      },
      trendingGridItemInner: {
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: 'full',
      },
      trendingGridImage: {
        aspectRatio: '1 / 1',
        backgroundColor: 'background.secondary',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
        w: 'full',
        sx: {
          img: {
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            width: '100%',
          },
        },
      },
      trendingGridInfo: {
        alignItems: 'flex-start',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        textAlign: 'left',
        width: 'full',
      },
      trendingGridTitle: {
        ...trendingProductTitleText,
        textAlign: 'left',
        whiteSpace: 'normal',
        width: 'full',
        wordBreak: 'break-word',
      },
      trendingGridCategory: {
        ...trendingProductCategoryText,
        overflow: 'hidden',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: 'full',
      },
      spotlightColumn: {
        flexShrink: 0,
        maxW: {base: 'full', xl: '291px'},
        minW: 0,
        width: {base: 'full', xl: '291px'},
      },
      spotlightList: {
        gap: '24px',
      },
      spotlightHeading: {
        ...searchSectionEyebrowText,
      },
      spotlightName: {
        ...spotlightArticleTitleText,
        fontVariantNumeric: {base: 'lining-nums proportional-nums', xl: 'unset'},
        lineHeight: {base: '120%', xl: '130%'},
        width: 'full',
      },
      trendingInfo: {
        alignItems: 'flex-start',
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        gap: '8px',
        justifyContent: 'flex-start',
        minW: 0,
        textAlign: 'left',
        width: 'full',
      },
      spotlightImage: {
        aspectRatio: {base: '16 / 9', xl: 'unset'},
        backgroundColor: 'greyscale90Alpha.10',
        flexShrink: 0,
        minH: {base: 'unset', xl: '176px'},
        overflow: 'hidden',
        position: 'relative',
        w: 'full',
        sx: {
          img: {
            height: '100%',
            minHeight: {base: 'unset', xl: '176px'},
            objectFit: 'cover',
            width: '100%',
          },
        },
      },
    },
    drawer: {
      root: {
        mt: header2Search.space.afterSearch,
      },
      layoutRow: {
        flexDirection: {base: 'column', md: 'column', lg: 'row'},
        gap: header2Search.space.columns,
        justifyContent: 'space-between',
        w: 'full',
      },
      mainColumn: {
        flex: {base: 'unset', md: 'unset', lg: '1 1 auto'},
        maxW: {base: 'full', md: 'full', lg: 'none'},
        minW: 0,
        width: {base: 'full', md: 'full', lg: 'auto'},
      },
      popularSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.section,
        width: 'full',
      },
      popularHeading: header2Search.type.section,
      popularTags: {
        gap: header2Search.space.chip,
        spacing: header2Search.space.chip,
        width: 'full',
      },
      popularTag: {
        ...popularTagBase,
        ...header2Search.type.chip,
        borderColor: header2Search.color.chipBorder,
        height: '32px',
        minH: '32px',
        px: 3,
        _hover: {
          bg: 'transparent',
          opacity: 1,
        },
      },
      trendingSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.section,
        width: 'full',
      },
      trendingHeading: header2Search.type.section,
      trendingList: {
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.row,
        width: 'full',
        sx: {
          ...header2Search.listHover,
          '& > *:nth-of-type(n+4)': {
            display: {base: 'none', lg: 'flex'},
          },
        },
      },
      trendingItem: {
        alignItems: 'center',
        bg: header2Search.color.rowBg,
        display: 'flex',
        h: {base: '56px', lg: '72px'},
        justifyContent: 'flex-start',
        minH: {base: '56px', lg: '72px'},
        overflow: 'hidden',
        p: 0,
        pl: 0,
        pr: 4,
        rounded: 'none',
        cursor: 'pointer',
        width: 'full',
        sx: {
          ...trendingButtonResetSx,
          minHeight: {base: '56px', lg: '72px'},
          padding: 0,
          paddingLeft: 0,
          paddingRight: '16px',
        },
        _active: {
          bg: header2Search.color.rowBg,
        },
        _focus: {
          boxShadow: 'none',
        },
        _focusVisible: {
          boxShadow: '0 0 0 3px rgba(27, 26, 24, 0.2)',
        },
        _hover: {
          bg: header2Search.color.rowBg,
          opacity: 1,
        },
      },
      trendingItemInner: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        gap: header2Search.space.rowGutter,
        minW: 0,
        pl: 0,
        pr: 0,
        width: 'full',
      },
      trendingImage: header2Search.productThumb,
      trendingInfo: {
        alignItems: 'center',
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        gap: {base: 2, lg: 0},
        justifyContent: 'space-between',
        minW: 0,
        textAlign: 'left',
      },
      trendingTitle: {
        ...header2Search.type.productTitle,
        flex: 1,
        minW: 0,
        overflow: 'hidden',
        textAlign: 'left',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      trendingCategory: {
        ...header2Search.type.productCategory,
        flexShrink: 0,
        maxW: {base: '96px', lg: '120px'},
        overflow: 'hidden',
        textAlign: 'right',
        textOverflow: 'ellipsis',
        w: {base: '96px', lg: '120px'},
        whiteSpace: 'nowrap',
      },
      spotlightColumn: {
        display: 'flex',
        flex: {base: 'unset', md: 'unset', lg: '0 0 294px'},
        flexDirection: 'column',
        gap: header2Search.space.headingToMedia,
        ml: {lg: 'auto'},
        maxW: {base: 'full', md: 'full', lg: '294px'},
        width: {base: 'full', md: 'full', lg: '294px'},
      },
      spotlightHeading: header2Search.type.section,
      spotlightList: {
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.spotlightList,
        sx: header2Search.listHover,
      },
      spotlightFirstContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.spotlightCaption,
        width: 'full',
      },
      spotlightHeadingBlock: {
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.headingToMedia,
        width: 'full',
      },
      spotlightItem: {
        display: 'flex',
        flexDirection: 'column',
        gap: header2Search.space.spotlightCaption,
        width: 'full',
      },
      spotlightImage: {
        aspectRatio: '800 / 533',
        backgroundColor: 'whiteAlpha.400',
        display: 'block',
        flexShrink: 0,
        minH: {base: 'unset', lg: '196px'},
        overflow: 'hidden',
        position: 'relative',
        w: 'full',
        sx: {
          img: {
            height: '100%',
            minHeight: {base: 'unset', lg: '196px'},
            objectFit: 'cover',
            width: '100%',
          },
        },
      },
      spotlightName: {
        ...header2Search.type.spotlightTitle,
        width: 'full',
      },
      spotlightCard: {
        _hover: {
          bg: 'transparent',
          opacity: 1,
          textDecoration: 'none',
        },
      },
    },
  },
});
